import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Monitor, Phone, User, X } from 'lucide-react';
import { PageHeader, Section, StatTile } from '../components/Scaffold';
import useAuthStore from '../stores/authStore';
import api from '../api/client';
import { subscribe as subscribeAdminWs } from '../utils/wsAdmin';

// role in ("platform_owner", "platform_user") — see User.access_level's
// comment in backend/app/models.py for why these Owner/User accounts aren't
// literally "owner"/"user" in the DB.
const PLATFORM_ACCESS_ROLES = ['platform_owner', 'platform_user'];

/**
 * Cross-tenant support ticket queue.
 *
 * A café's own staff raise a ticket (primus-admin-main, Settings > Support
 * Ticket); SuperAdmin sees and manages every café's tickets here. Backed by
 * a global-DB table (backend/app/db/models_global.py: PlatformSupportTicket)
 * — separate from the per-café app.db.models_cafe.SupportTicket, which
 * SuperAdmin's token can't reach (no cafe_id).
 *
 * Endpoint: GET /api/internal/support-tickets, POST /api/internal/support-tickets/{id}
 * for status updates (superadmin-only). Editing/withdrawing a ticket's own
 * content (subject/priority) is done by the reporting café itself, in
 * primus-admin-main's Settings > Support page, via
 * app.api.endpoints.platform_support_tickets — not here.
 */
const PRIORITY_COLOR = { urgent: 'danger', high: 'warning', low: 'neutral', medium: 'info' };
const PRIORITY_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };
// "resolved" was dropped as a separate status (user directive, 2026-10-09) --
// a ticket is open, in_progress, or closed. "closed" takes over the "done"
// meaning resolved used to have, hence the green (success) it inherits here.
const STATUS_COLOR = { open: 'info', in_progress: 'warning', closed: 'success' };
const STATUS_OPTIONS = ['open', 'in_progress', 'closed'];
// What kind of ticket it is — tagged here by the platform team (the café does not choose). It feeds the
// Weekly MIS "Complaints" and "Feature requests" rows; an untagged ticket counts toward neither.
const CATEGORY_OPTIONS = [
  { value: 'complaint', label: 'Complaint' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'bug', label: 'Bug' },
  { value: 'question', label: 'Question' },
  { value: 'other', label: 'Other' },
];

// A ticket description longer than this is folded to 3 lines in the ticket
// window, with "Show more" (roughly 3 lines of text at the window's width).
const DESC_FOLD_CHARS = 220;
const DESC_FOLD_LINES = 3;

// How a SuperAdmin login's role reads next to their name in the assign list.
const ROLE_LABEL = { superadmin: 'Superadmin', admin: 'Admin', platform_owner: 'Owner', platform_user: 'User' };

// Ticket text arrives HTML-escaped by the backend's input sanitiser (an
// apostrophe shows as "&#x27;"). A <textarea> decodes entities without ever
// running markup, and React escapes the result again on render.
// Decoded until it stops changing: a café editing its ticket re-escapes the
// already-escaped subject ("isn&amp;#x27;t"), so one pass is not enough. Same
// rule as backend app/services/ticket_alerts.unescape_ticket_text.
function decodeEntities(text) {
  if (!text) return text;
  const el = document.createElement('textarea');
  for (let i = 0; i < 5 && text.includes('&'); i += 1) {
    el.innerHTML = text;
    if (el.value === text) break;
    text = el.value;
  }
  return text;
}

// A ticket's PCs as text -- "All PCs", "PC-01, PC-02", or with `max`
// "PC-01, PC-02 +3". Null when the café picked none (help-chat tickets,
// tickets raised before PCs could be picked).
function pcsLabel(pcs, max = Infinity) {
  if (pcs === 'all') return 'All PCs';
  if (!Array.isArray(pcs) || pcs.length === 0) return null;
  const names = pcs.map((p) => p.name);
  return names.length > max ? `${names.slice(0, max).join(', ')} +${names.length - max}` : names.join(', ');
}

function parseUtc(iso) {
  return new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
}

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
// When a ticket counts as waiting too long -- the same limits as the Teams
// reminders (backend app/tasks/ticket_reminders.py): nobody has taken it after
// 15 minutes, or it is still not closed this long after being raised.
const PICKUP_WITHIN_MS = 15 * 60 * 1000;
const CLOSE_WITHIN_MS = { urgent: 2 * HOUR, high: 4 * HOUR, medium: DAY, low: 3 * DAY };
// Closed tickets shown under the open ones before "Show N more closed".
const CLOSED_PREVIEW = 5;

const ageMs = (t) => Date.now() - parseUtc(t.created_at).getTime();
const closedAtMs = (t) => parseUtc(t.resolved_at || t.updated_at).getTime();

// Where a ticket sits in the queue: its priority, moved up the longer it
// waits so an old ticket can't sit at the bottom forever behind newer urgent
// ones -- Low counts as Medium after 3 days, Medium as High after 7. Ordering
// only: the café's priority itself is not changed.
function queueRank(t) {
  let rank = PRIORITY_RANK[t.priority] ?? PRIORITY_RANK.medium;
  const days = ageMs(t) / DAY;
  if (rank === PRIORITY_RANK.low && days >= 3) rank = PRIORITY_RANK.medium;
  if (rank === PRIORITY_RANK.medium && days >= 7) rank = PRIORITY_RANK.high;
  return rank;
}

function isOverdue(t) {
  if (t.status === 'closed') return false;
  if (!t.assigned_staff) return ageMs(t) >= PICKUP_WITHIN_MS;
  return ageMs(t) >= (CLOSE_WITHIN_MS[t.priority] ?? CLOSE_WITHIN_MS.medium);
}

// "40 min", "5 hours", "3 days".
function durationText(ms) {
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return hours === 1 ? '1 hour' : `${hours} hours`;
  return `${Math.round(hours / 24)} days`;
}

// What the list shows (statusFilter). No tab row: the number cards set
// needs / progress / urgent / closed (click again to go back), the "Assigned to
// me" toggle sets mine. The page opens on 'open' — every ticket still needing
// someone, closed ones a click away on the Closed card. 'all' is only for a
// deep-linked ticket, which may be an old closed one.
// The three sections of the queue. "Needs someone" is any ticket not closed
// with nobody on it; once someone is assigned it is "In progress", whatever
// its status says -- someone owns it.
const SECTION_OF = (t) => (t.status === 'closed' ? 'closed' : t.assigned_staff ? 'progress' : 'needs');
const SECTIONS = [
  ['needs', 'Needs someone', 'unassigned · most urgent first, then oldest'],
  ['progress', 'In progress', 'yours first'],
  ['closed', 'Closed', 'most recently closed first'],
];

function fmtDateTime(iso) {
  return iso ? parseUtc(iso).toLocaleString() : '—';
}

function timeAgo(iso) {
  const d = parseUtc(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function SupportTickets() {
  const { isSuperAdmin, user } = useAuthStore();
  const isPlatformRole = PLATFORM_ACCESS_ROLES.includes(user?.role);
  const canWrite = isSuperAdmin() || (isPlatformRole && user?.access_level === 'write');

  // Deep link from a Teams ticket alert's "Open in SuperAdmin" button
  // (backend/app/services/ticket_alerts.py): /support?ticket=<id> opens that
  // ticket's detail window, and highlights its row behind it, regardless of
  // which status tab it's under.
  const [searchParams] = useSearchParams();
  const linkedTicketId = searchParams.get('ticket');
  const [highlightId, setHighlightId] = useState(linkedTicketId ? Number(linkedTicketId) : null);

  const [tickets, setTickets] = useState([]);
  const [cafes, setCafes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [search, setSearch] = useState('');
  // null = the sections; {key, dir} = the whole list sorted by one column.
  const [sortBy, setSortBy] = useState(null);
  // A deep-linked ticket may be an older closed one, so its row must not be
  // folded away under "Show N more closed".
  const [showAllClosed, setShowAllClosed] = useState(Boolean(linkedTicketId));
  const [updating, setUpdating] = useState(null);
  // The ticket whose detail window is open (clicking a row opens it). Starts
  // on the deep-linked ticket; the window renders once that ticket has loaded
  // (openTicket is derived from the fetched list), and never if it's gone.
  const [openId, setOpenId] = useState(linkedTicketId ? Number(linkedTicketId) : null);
  const openTicket = tickets.find((t) => t.id === openId) || null;
  // Edits made in the detail window, not yet saved: only the fields the user
  // has touched ({status?, priority?, assigned_staff?, category?}). Nothing is sent until
  // Save, so several changes are ONE update -- and one Teams card (the backend
  // folds a status + assignee change into the status card). Closing the
  // window without saving discards them.
  const [draft, setDraft] = useState({});
  const [saveError, setSaveError] = useState(null);
  // A long description is folded to a few lines so the controls below it stay
  // on screen without scrolling; "Show more" unfolds it. Folded again for
  // every ticket opened.
  const [descExpanded, setDescExpanded] = useState(false);

  const openTicketWindow = (id) => { setOpenId(id); setDraft({}); setSaveError(null); setDescExpanded(false); };
  const closeTicketWindow = () => { setOpenId(null); setDraft({}); setSaveError(null); setDescExpanded(false); };

  useEffect(() => {
    if (!openId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpenId(null); setDraft({}); setSaveError(null); setDescExpanded(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openId]);

  const fetchAll = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [ticketsRes, cafesRes, staffRes] = await Promise.all([
        api.get('/internal/support-tickets'),
        api.get('/internal/cafes'),
        api.get('/internal/users'),
      ]);
      setTickets(ticketsRes.data || []);
      setCafes(cafesRes.data || []);
      setStaff(staffRes.data || []);
    } catch (err) {
      console.error('Failed to fetch support tickets:', err);
      setError(err.response?.data?.detail || 'Failed to load support tickets');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Live: a ticket raised while this page is open shows up without a manual
  // refresh, same event the header's alert toast/bell listen for.
  useEffect(() => {
    const unsub = subscribeAdminWs((msg) => {
      if (msg?.event === 'ticket.created') fetchAll();
    });
    return () => { unsub && unsub(); };
  }, []);

  // A deep-linked ticket must be visible regardless of which status it's in
  // -- "all" overrides whatever tab a previous visit left selected.
  useEffect(() => {
    if (highlightId) setStatusFilter('all');
  }, [highlightId]);

  // Scroll the linked row into view once it's actually on the page (after
  // the fetch resolves). Re-runs if the row wasn't there yet (e.g. the
  // first paint raced the fetch) and becomes available on a later render.
  useEffect(() => {
    if (!highlightId || isLoading) return;
    const row = document.getElementById(`ticket-row-${highlightId}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightId, isLoading, tickets]);

  const cafeName = useMemo(() => {
    const map = new Map(cafes.map((c) => [c.id, c.name]));
    return (id) => map.get(id) || `Café ${id}`;
  }, [cafes]);

  const staffName = useMemo(() => {
    const map = new Map(staff.map((s) => [s.id, s.name || s.email]));
    return (id) => (id ? map.get(id) || `User ${id}` : 'Unassigned');
  }, [staff]);

  // Anyone with a SuperAdmin login can be handed a ticket -- the same list
  // the Users page shows (GET /internal/users: no café staff).
  const assignable = useMemo(
    () => [...staff].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)),
    [staff],
  );

  const cafeContact = useMemo(() => {
    const map = new Map(cafes.map((c) => [c.id, c]));
    return (id) => map.get(id) || {};
  }, [cafes]);

  const inTab = (t, tab) => {
    if (tab === 'all') return true;
    if (tab === 'open') return t.status !== 'closed';
    if (tab === 'mine') return t.assigned_staff === user?.id;
    if (tab === 'urgent') return t.priority === 'urgent' && t.status !== 'closed';
    return SECTION_OF(t) === tab;
  };

  const mineCount = useMemo(
    () => tickets.filter((t) => t.assigned_staff === user?.id).length,
    [tickets, user?.id],
  );

  // Search: ticket number, café, subject, description, PCs or who it's assigned to.
  // A café quotes its own number ("ticket 3"), so "TEST 6 #3" finds it; the
  // T-number works too.
  const matchesSearch = (t) => {
    const raw = search.trim().toLowerCase();
    if (!raw) return true;
    const q = raw.replace(/^#/, '');
    const own = t.cafe_ticket_no ? [`${cafeName(t.cafe_id)} #${t.cafe_ticket_no}`.toLowerCase(), `#${t.cafe_ticket_no}`] : [];
    if (own.some((s) => s === raw || s.startsWith(`${raw} `) || s.endsWith(raw))) return true;
    return [
      `t-${t.id}`, String(t.id), cafeName(t.cafe_id), decodeEntities(t.subject),
      decodeEntities(t.description), t.assigned_staff ? staffName(t.assigned_staff) : '',
      pcsLabel(t.pcs),
    ].some((s) => (s || '').toLowerCase().includes(q));
  };

  // Order within each section. Needs someone: most urgent first (after the
  // move-up rule), then the one that has waited longest. In progress: mine
  // first, then the same. Closed: most recently closed first.
  const sectionOrder = (a, b) => {
    const sa = SECTION_OF(a);
    const sb = SECTION_OF(b);
    if (sa !== sb) return SECTIONS.findIndex(([k]) => k === sa) - SECTIONS.findIndex(([k]) => k === sb);
    if (sa === 'closed') return closedAtMs(b) - closedAtMs(a);
    if (sa === 'progress') {
      const mine = (b.assigned_staff === user?.id) - (a.assigned_staff === user?.id);
      if (mine) return mine;
    }
    return (queueRank(a) - queueRank(b)) || (ageMs(b) - ageMs(a));
  };

  // A column header click sorts the whole list by that column instead of the
  // sections; again reverses it; a third time goes back to the sections.
  const columnOrder = (a, b) => {
    const { key, dir } = sortBy;
    let d = 0;
    if (key === 'cafe') d = cafeName(a.cafe_id).localeCompare(cafeName(b.cafe_id));
    else if (key === 'priority') d = (PRIORITY_RANK[a.priority] ?? 2) - (PRIORITY_RANK[b.priority] ?? 2);
    else if (key === 'age') d = ageMs(b) - ageMs(a);
    return d * dir || sectionOrder(a, b);
  };
  const cycleSort = (key) => setSortBy((s) => (
    !s || s.key !== key ? { key, dir: 1 } : s.dir === 1 ? { key, dir: -1 } : null
  ));
  const sortMark = (key) => (sortBy?.key === key ? (sortBy.dir === 1 ? '↓' : '↑') : '↕');

  // The table's rows: section headers, tickets, and "Show N more closed".
  const rows = useMemo(() => {
    // A search from the default view looks through every ticket, closed ones
    // included — someone typing an old ticket number expects to find it.
    const view = statusFilter === 'open' && search.trim() ? 'all' : statusFilter;
    const list = tickets.filter((t) => inTab(t, view) && matchesSearch(t));
    if (sortBy) return [...list].sort(columnOrder).map((t) => ({ t }));
    const sorted = [...list].sort(sectionOrder);
    // A single-section tab needs no headers.
    if (!['all', 'open', 'mine', 'urgent'].includes(statusFilter)) return sorted.map((t) => ({ t }));
    const out = [];
    for (const [key, label, hint] of SECTIONS) {
      const part = sorted.filter((t) => SECTION_OF(t) === key);
      if (!part.length) continue;
      out.push({ section: key, label, hint, count: part.length });
      const folded = key === 'closed' && !showAllClosed && !search.trim() && part.length > CLOSED_PREVIEW;
      (folded ? part.slice(0, CLOSED_PREVIEW) : part).forEach((t) => out.push({ t }));
      if (folded) out.push({ more: part.length - CLOSED_PREVIEW });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, statusFilter, search, sortBy, showAllClosed, user?.id, cafeName, staffName]);
  const ticketRowCount = rows.filter((r) => r.t).length;

  const stats = useMemo(() => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * DAY;
    const thirtyDaysAgo = now - 30 * DAY;
    // Average time from raised to closed, over tickets closed in the last 30 days.
    const recentlyClosed = tickets.filter((t) => t.status === 'closed' && t.resolved_at && closedAtMs(t) >= thirtyDaysAgo);
    const avgCloseMs = recentlyClosed.length
      ? recentlyClosed.reduce((sum, t) => sum + (closedAtMs(t) - parseUtc(t.created_at).getTime()), 0) / recentlyClosed.length
      : null;
    return {
      needs: tickets.filter((t) => SECTION_OF(t) === 'needs').length,
      progress: tickets.filter((t) => SECTION_OF(t) === 'progress').length,
      urgent: tickets.filter((t) => t.priority === 'urgent' && t.status !== 'closed').length,
      closed7d: tickets.filter((t) => t.status === 'closed' && closedAtMs(t) >= sevenDaysAgo).length,
      avgCloseMs,
      avgCloseCount: recentlyClosed.length,
    };
  }, [tickets]);

  // What the detail window shows for a field: the unsaved edit if there is
  // one, else the ticket's saved value.
  const shown = (field) => (field in draft ? draft[field] : openTicket?.[field] ?? null);

  // Only fields whose edited value differs from what's saved -- picking a
  // value and then picking the original back again is not a change.
  const pendingChanges = openTicket
    ? Object.fromEntries(
      Object.entries(draft).filter(([field, value]) => value !== (openTicket[field] ?? null)),
    )
    : {};
  const hasChanges = Object.keys(pendingChanges).length > 0;

  // ONE update per press, so one Teams card at most. An omitted field means
  // "leave alone" server-side; a present null clears it (unassign / untag).
  const saveTicket = async (id, changes) => {
    if (!Object.keys(changes).length) return;
    setUpdating(id);
    setSaveError(null);
    try {
      await api.post(`/internal/support-tickets/${id}`, changes);
      await fetchAll();
      closeTicketWindow();
    } catch (err) {
      console.error('Failed to update ticket:', err);
      setSaveError(err.response?.data?.detail || 'Could not save. Try again.');
    } finally {
      setUpdating(null);
    }
  };

  // "Take this ticket": assign it to me AND start it, as one update -- the
  // most common pick-up, which used to be two saves and two Teams cards.
  // Sends only what actually changes, and discards any other unsaved edits.
  const takeTicket = (t) => {
    const changes = {};
    if (t.assigned_staff !== user?.id) changes.assigned_staff = user?.id;
    if (t.status !== 'in_progress') changes.status = 'in_progress';
    saveTicket(t.id, changes);
  };
  const canTake = (t) => canWrite && user?.id != null && t.status !== 'closed'
    && (t.assigned_staff !== user.id || t.status !== 'in_progress');

  return (
    <div className="page support-tickets-page">
      <style>{`
        .support-tickets-page,
        .support-ticket-window {
          /* noLag rebrand: scoped to this page only (and its ticket window,
             which is portalled to <body> so doesn't inherit from the page).
             No Figma node exists
             for Support Tickets — applied by the same accent-only pattern
             as CafeOnboard/Licenses/Billing/Announcements rather than
             verified against a design. PRIORITY_COLOR and STATUS_COLOR
             below are separate --status-* tokens, untouched by this — the
             active filter tab (.btn--primary) is the only thing that
             flows to orange, and that's a selection state, not a status. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);
          /* Link hover (a:hover) and the field focus ring are defined on
             :root in terms of the old accent, so they resolve to blue/purple
             there -- restate them here or they stay off-brand. */
          --accent-secondary: #FF9A3D;
          --shadow-focus: 0 0 0 3px rgba(254, 123, 0, 0.25);
        }
        /* The row a Teams ticket alert's "Open in SuperAdmin" button linked
           to (?ticket=<id>) -- a brief flash so it's easy to spot on arrival,
           settling into a lasting accent border for the rest of the visit. */
        .ticket-row--linked {
          background: var(--accent-primary-subtle);
          box-shadow: inset 3px 0 0 var(--accent-primary);
          animation: ticket-row-flash 1.6s ease-out;
        }
        @keyframes ticket-row-flash {
          0% { background: var(--accent-primary-glow); }
          100% { background: var(--accent-primary-subtle); }
        }
        /* Five tiles on one row. The shared .stat-grid wants 180px a tile, so on
           a laptop the fifth (Avg time to close) dropped to a row of its own;
           this page only, Billing and Roles keep the shared grid. */
        .support-tickets-page .ticket-stat-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); }
        @media (max-width: 900px) {
          .support-tickets-page .ticket-stat-grid { grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); }
        }
        /* The ticket list runs down the page — one scrollbar, the page's — with
           its column headings pinned to the top of the screen while it scrolls.
           Sticky needs every box between the headings and the page to be a
           non-scrolling one, hence overflow: clip (clips to the rounded corners
           without becoming a scroll area) instead of the card's usual hidden. */
        .support-tickets-page .ticket-table-card { overflow: clip; }
        .support-tickets-page .ticket-table-wrap thead th {
          position: sticky; top: 0; z-index: 2;
          background: #15171f;
          box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        }
        /* The table can never widen the page: width 0 + min-width 100% keeps it
           out of the layout's sizing (as on the café page's PC list). Where the
           window is too narrow for its columns, it scrolls sideways inside its
           card instead — and only there, because a sideways-scrolling box
           can't also have headings that stick to the page. */
        .support-tickets-page .ticket-table-wrap { width: 0; min-width: 100%; }
        @media (max-width: 1199px) {
          .support-tickets-page .ticket-table-wrap { overflow-x: auto; }
        }
        /* Number tiles double as filters: a plain button around StatTile. */
        .support-tickets-page .ticket-tile {
          all: unset;
          display: block;
          cursor: pointer;
          border-radius: var(--radius-lg);
        }
        .support-tickets-page .ticket-tile .stat-tile { height: 100%; box-sizing: border-box; transition: border-color 0.15s; }
        .support-tickets-page .ticket-tile:hover .stat-tile { border-color: var(--accent-primary-glow); }
        .support-tickets-page .ticket-tile--on .stat-tile { border-color: var(--accent-primary); background: var(--accent-primary-subtle); }
        .support-tickets-page .ticket-tile:focus-visible { box-shadow: var(--shadow-focus); }
        .support-tickets-page .ticket-tab-count { margin-left: 6px; opacity: 0.75; font-weight: 600; }
        .support-tickets-page .ticket-search { margin-left: auto; width: 260px; max-width: 100%; }
        .support-tickets-page .ticket-sort {
          all: unset;
          cursor: pointer;
          white-space: nowrap;
        }
        .support-tickets-page .ticket-sort:hover { color: var(--accent-primary); }
        /* Section headers read as headings, not as another ticket row: bigger
           title, a bar in the section's colour, the count in a pill, and room
           above each one. */
        .support-tickets-page .ticket-section td {
          background: rgba(255, 255, 255, 0.04);
          padding-top: 18px;
          padding-bottom: 10px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.16);
          cursor: default;
        }
        .support-tickets-page .ticket-section__in {
          display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
          padding-left: 10px; border-left: 4px solid rgba(255, 255, 255, 0.3);
        }
        .support-tickets-page .ticket-section__title { font-size: 15px; font-weight: 700; color: var(--text-primary); }
        .support-tickets-page .ticket-section__count {
          font-size: 12px; font-weight: 600; padding: 1px 9px; border-radius: 999px;
          background: rgba(255, 255, 255, 0.08); color: var(--text-secondary);
        }
        .support-tickets-page .ticket-section__hint { font-size: 12px; color: var(--text-tertiary); }
        .support-tickets-page .ticket-section__in--needs { border-left-color: #f59e0b; }
        .support-tickets-page .ticket-section__in--needs .ticket-section__count { background: rgba(245, 158, 11, 0.16); color: #fbbf24; }
        .support-tickets-page .ticket-section__in--progress { border-left-color: #FE7B00; }
        .support-tickets-page .ticket-section__in--progress .ticket-section__count { background: rgba(254, 123, 0, 0.16); color: #FE7B00; }
        .support-tickets-page .ticket-more td { text-align: center; }
        .support-tickets-page .ticket-more button {
          background: none;
          border: none;
          color: var(--accent-primary);
          cursor: pointer;
          font-size: 13px;
        }
        .support-tickets-page .ticket-row--closed td { opacity: 0.55; }
        .support-tickets-page .ticket-age--late { color: var(--status-danger, #ef4444); font-weight: 600; }
        .support-tickets-page .ticket-you { color: var(--accent-primary); font-weight: 600; }
        .support-tickets-page .ticket-cafe-no,
        .support-ticket-window .ticket-cafe-no { color: var(--text-tertiary); font-weight: 400; }
        /* Ticket window: header and Save bar stay put, only the middle
           scrolls -- the shared .modal scrolls as a whole, which pushed Save
           below the fold on a laptop screen. Slightly tighter padding than
           the shared modal so most tickets fit without scrolling at all. */
        .support-ticket-window {
          display: flex;
          flex-direction: column;
          overflow: hidden;
          max-height: calc(100vh - 32px);
        }
        .support-ticket-window .modal__header,
        .support-ticket-window .modal__footer {
          flex-shrink: 0;
          padding: 14px 20px;
        }
        .support-ticket-window .modal__body {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          padding: 16px 20px;
        }
        .support-ticket-window .ticket-desc--folded {
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
          line-clamp: 3;
          overflow: hidden;
        }
        .support-ticket-window .ticket-desc-toggle {
          margin-top: 4px;
          padding: 0;
          background: none;
          border: none;
          color: var(--accent-primary);
          font-size: 13px;
          cursor: pointer;
        }
        .support-ticket-window .ticket-desc-toggle:hover {
          color: var(--accent-secondary);
        }
        /* The ticket's PCs: a small muted line under the subject in the
           queue, clickable chips in the ticket window. */
        .ticket-pcs-line {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 2px;
          font-size: 12px;
          color: var(--text-tertiary);
        }
        .support-ticket-window .ticket-pcs {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }
        .support-ticket-window .ticket-pc-chip {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 2px 9px;
          border-radius: 999px;
          font-size: 12px;
          color: var(--text-secondary);
          background: rgba(148, 163, 184, 0.12);
          border: 1px solid rgba(148, 163, 184, 0.3);
          text-decoration: none;
        }
        .support-ticket-window .ticket-pc-chip:hover {
          color: var(--accent-primary);
          border-color: var(--accent-primary);
        }
        /* "Take this ticket": orange outline, so it reads as orange next to
           the solid orange Save without two filled buttons competing. */
        .support-ticket-window .ticket-take-btn {
          color: var(--accent-primary);
          border-color: var(--accent-primary);
        }
        .support-ticket-window .ticket-take-btn:hover:not(:disabled) {
          background: var(--accent-primary-subtle);
          border-color: var(--accent-primary);
        }
      `}</style>
      <PageHeader
        title="Support Tickets"
        subtitle="Cross-tenant support queue for the platform team"
      />

      {/* Each number is also a shortcut to the tickets behind it. */}
      <div className="stat-grid ticket-stat-grid">
        {[
          ['needs', 'Needs someone', stats.needs, 'Nobody assigned yet'],
          ['progress', 'In progress', stats.progress, null],
          ['urgent', 'Urgent', stats.urgent, 'Not closed yet'],
          ['closed', 'Closed (7d)', stats.closed7d, null],
        ].map(([tab, label, value, hint]) => (
          <button
            key={tab}
            type="button"
            className={`ticket-tile${statusFilter === tab ? ' ticket-tile--on' : ''}`}
            onClick={() => { setStatusFilter(statusFilter === tab ? 'open' : tab); setSortBy(null); }}
          >
            <StatTile label={label} value={value} hint={hint} />
          </button>
        ))}
        <StatTile
          label="Avg time to close"
          value={stats.avgCloseMs == null ? '—' : durationText(stats.avgCloseMs)}
          hint={stats.avgCloseCount ? `Last 30 days · ${stats.avgCloseCount} ticket${stats.avgCloseCount === 1 ? '' : 's'}` : 'Nothing closed in 30 days'}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, margin: '16px 0', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* On: only your tickets. Off: back to the open queue. */}
        <button
          type="button"
          className={`btn btn--sm ${statusFilter === 'mine' ? 'btn--primary' : 'btn--secondary'}`}
          aria-pressed={statusFilter === 'mine'}
          onClick={() => { setStatusFilter(statusFilter === 'mine' ? 'open' : 'mine'); setSortBy(null); }}
        >
          Assigned to me
          <span className="ticket-tab-count">{mineCount}</span>
        </button>
        <input
          type="search"
          className="field__input ticket-search"
          placeholder="Search ticket, café, subject, person…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="card table-card ticket-table-card">
        <div className="ticket-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Ticket</th>
              <th><button type="button" className="ticket-sort" onClick={() => cycleSort('cafe')}>Café {sortMark('cafe')}</button></th>
              <th>Subject</th>
              <th><button type="button" className="ticket-sort" onClick={() => cycleSort('priority')}>Priority {sortMark('priority')}</button></th>
              <th>Status</th>
              <th>Assigned</th>
              <th><button type="button" className="ticket-sort" onClick={() => cycleSort('age')}>Age {sortMark('age')}</button></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="text-tertiary">Loading…</td></tr>
            ) : error ? (
              <tr><td colSpan={7} className="text-tertiary">{error}</td></tr>
            ) : ticketRowCount === 0 ? (
              <tr><td colSpan={7} className="text-tertiary">{search.trim() ? 'No tickets match your search.' : statusFilter === 'open' ? 'No open tickets.' : 'No tickets.'}</td></tr>
            ) : rows.map((r) => {
              if (r.section) {
                return (
                  <tr key={`section-${r.section}`} className="ticket-section">
                    <td colSpan={7}>
                      <div className={`ticket-section__in ticket-section__in--${r.section}`}>
                        <span className="ticket-section__title">{r.label}</span>
                        <span className="ticket-section__count">{r.count}</span>
                        <span className="ticket-section__hint">{r.hint}</span>
                      </div>
                    </td>
                  </tr>
                );
              }
              if (r.more) {
                return (
                  <tr key="more-closed" className="ticket-more">
                    <td colSpan={7}>
                      <button type="button" onClick={() => setShowAllClosed(true)}>
                        Show {r.more} more closed
                      </button>
                    </td>
                  </tr>
                );
              }
              const { t } = r;
              const closed = t.status === 'closed';
              const overdue = isOverdue(t);
              const classes = [highlightId === t.id && 'ticket-row--linked', closed && 'ticket-row--closed'].filter(Boolean).join(' ');
              return (
                // Read-only at a glance; everything else (contact, description,
                // status/assign/category controls) is in the detail window.
                <tr
                  key={t.id}
                  id={`ticket-row-${t.id}`}
                  className={classes || undefined}
                  style={{ cursor: 'pointer' }}
                  onClick={() => openTicketWindow(t.id)}
                >
                  <td className="font-mono" style={{ whiteSpace: 'nowrap' }}>
                    {closed ? '✓ ' : ''}T-{t.id}
                    {/* The café's own number for it -- what they quote when they call. */}
                    {t.cafe_ticket_no && <span className="ticket-cafe-no"> · #{t.cafe_ticket_no}</span>}
                  </td>
                  <td>{cafeName(t.cafe_id)}</td>
                  <td className="text-secondary">
                    {decodeEntities(t.subject)}
                    {pcsLabel(t.pcs) && (
                      <div className="ticket-pcs-line" title={pcsLabel(t.pcs)}>
                        <Monitor size={11} /> {pcsLabel(t.pcs, 3)}
                      </div>
                    )}
                  </td>
                  <td><span className={`badge badge--${closed ? 'neutral' : PRIORITY_COLOR[t.priority]}`}>{t.priority}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span className={`badge badge--${closed ? 'neutral' : STATUS_COLOR[t.status]}`}>{t.status.replace('_', ' ')}</span>
                  </td>
                  <td className={t.assigned_staff ? 'text-secondary' : 'text-tertiary'}>
                    {t.assigned_staff && t.assigned_staff === user?.id ? <span className="ticket-you">You</span> : staffName(t.assigned_staff)}
                  </td>
                  <td
                    className={overdue ? 'ticket-age--late' : 'text-tertiary'}
                    style={{ whiteSpace: 'nowrap' }}
                    title={overdue
                      ? (t.assigned_staff ? 'Not closed in time' : 'Nobody has taken it for over 15 minutes')
                      : undefined}
                  >
                    {closed ? `Closed ${timeAgo(t.resolved_at || t.updated_at)}` : timeAgo(t.created_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {openTicket && createPortal(
        <div className="modal-backdrop" onClick={closeTicketWindow}>
          <div
            className="modal support-ticket-window"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ticket-detail-title"
            style={{ maxWidth: 560, width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal__header">
              <h3 className="modal__title" id="ticket-detail-title">
                T-{openTicket.id}
                {openTicket.cafe_ticket_no && (
                  <span className="ticket-cafe-no"> · {cafeName(openTicket.cafe_id)} #{openTicket.cafe_ticket_no}</span>
                )}
              </h3>
              <button type="button" className="modal__close" onClick={closeTicketWindow} title="Close">
                <X size={18} />
              </button>
            </div>
            <div className="modal__body">
              <h4 style={{ margin: '0 0 8px' }}>{decodeEntities(openTicket.subject)}</h4>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <span className={`badge badge--${PRIORITY_COLOR[openTicket.priority]}`}>{openTicket.priority}</span>
                <span className={`badge badge--${STATUS_COLOR[openTicket.status]}`}>{openTicket.status.replace('_', ' ')}</span>
                {openTicket.category && (
                  <span className="badge badge--neutral">
                    {CATEGORY_OPTIONS.find((c) => c.value === openTicket.category)?.label || openTicket.category}
                  </span>
                )}
              </div>
              {/* The café's PCs for this ticket; each opens that PC on the café
                  page (by id, so a rename doesn't lose it), "All PCs" opens the
                  café's remote tiles. */}
              <div className="ticket-pcs" style={{ marginBottom: 12 }}>
                <span className="text-tertiary" style={{ fontSize: 12 }}>PCs</span>
                {openTicket.pcs === 'all' ? (
                  <Link className="ticket-pc-chip" to={`/cafes/${openTicket.cafe_id}/remote`} onClick={closeTicketWindow}>
                    <Monitor size={12} /> All PCs
                  </Link>
                ) : Array.isArray(openTicket.pcs) && openTicket.pcs.length > 0 ? (
                  openTicket.pcs.map((pc) => (
                    <Link
                      key={pc.id}
                      className="ticket-pc-chip"
                      to={`/cafes/${openTicket.cafe_id}?pcid=${pc.id}`}
                      onClick={closeTicketWindow}
                      title={`Open ${pc.name} on the café page`}
                    >
                      <Monitor size={12} /> {pc.name}
                    </Link>
                  ))
                ) : (
                  <span className="text-tertiary" style={{ fontSize: 13 }}>No PC picked</span>
                )}
              </div>
              {(() => {
                const text = decodeEntities(openTicket.description);
                if (!text) {
                  return <div className="text-tertiary" style={{ marginBottom: 16 }}>No description was given.</div>;
                }
                // "Long" by length or line count, decided from the text itself
                // rather than by measuring the DOM -- close enough for a fold,
                // and short tickets look exactly as before.
                const isLong = text.length > DESC_FOLD_CHARS || text.split('\n').length > DESC_FOLD_LINES;
                const folded = isLong && !descExpanded;
                return (
                  <div style={{ marginBottom: 16 }}>
                    <div className={folded ? 'ticket-desc--folded' : undefined} style={{ whiteSpace: 'pre-wrap' }}>
                      {text}
                    </div>
                    {isLong && (
                      <button
                        type="button"
                        className="ticket-desc-toggle"
                        onClick={() => setDescExpanded((v) => !v)}
                      >
                        {descExpanded ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                );
              })()}

              {(() => {
                const contact = cafeContact(openTicket.cafe_id);
                const phone = contact.owner_phone;
                return (
                  <div
                    className="text-secondary"
                    style={{ display: 'grid', gap: 4, fontSize: 13, paddingTop: 12, marginBottom: 16, borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.08))' }}
                  >
                    <Link to={`/cafes/${openTicket.cafe_id}`} style={{ fontWeight: 600, fontSize: 14 }}>
                      {cafeName(openTicket.cafe_id)}
                    </Link>
                    <div className={contact.location ? undefined : 'text-tertiary'}>
                      {contact.location || 'Location not set'}
                    </div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <span><User size={13} /> {contact.owner_name || '—'}</span>
                      <span>
                        <Phone size={13} />{' '}
                        {phone ? <a href={`tel:${phone.replace(/[^\d+]/g, '')}`}>{phone}</a> : '—'}
                      </span>
                    </div>
                    {openTicket.raised_by_name && (
                      <div>Raised by <span style={{ color: 'var(--text-primary)' }}>{openTicket.raised_by_name}</span></div>
                    )}
                  </div>
                );
              })()}

              {canTake(openTicket) && (
                <button
                  type="button"
                  className="btn btn--secondary btn--sm ticket-take-btn"
                  style={{ marginBottom: 12 }}
                  disabled={updating === openTicket.id}
                  title="Assign it to you and mark it in progress, in one step"
                  onClick={() => takeTicket(openTicket)}
                >
                  Take this ticket
                </button>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 10, alignItems: 'center', marginBottom: 16 }}>
                <span className="text-tertiary">Status</span>
                <select
                  className="field__select"
                  value={shown('status')}
                  disabled={!canWrite || updating === openTicket.id}
                  title={!canWrite ? 'Read-only access' : undefined}
                  onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s.replace('_', ' ')}</option>
                  ))}
                </select>
                {/* The café picks a priority when raising it; we have the final
                    say -- the queue order and the Teams reminders follow this. */}
                <span className="text-tertiary">Priority</span>
                <select
                  className="field__select"
                  value={shown('priority') || 'medium'}
                  disabled={!canWrite || updating === openTicket.id}
                  title={!canWrite ? 'Read-only access' : 'Correct the priority the café picked'}
                  onChange={(e) => setDraft((d) => ({ ...d, priority: e.target.value }))}
                >
                  {Object.keys(PRIORITY_RANK).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <span className="text-tertiary">Assigned to</span>
                <select
                  className="field__select"
                  value={shown('assigned_staff') || ''}
                  disabled={!canWrite || updating === openTicket.id}
                  title={!canWrite ? 'Read-only access' : undefined}
                  onChange={(e) => setDraft((d) => ({ ...d, assigned_staff: e.target.value ? Number(e.target.value) : null }))}
                >
                  <option value="">Unassigned</option>
                  {assignable.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.email}{ROLE_LABEL[u.role] ? ` (${ROLE_LABEL[u.role]})` : ''}
                    </option>
                  ))}
                </select>
                <span className="text-tertiary">Category</span>
                <select
                  className="field__select"
                  value={shown('category') || ''}
                  disabled={!canWrite || updating === openTicket.id}
                  title={!canWrite ? 'Read-only access' : 'Tag the kind of ticket (used by the Weekly MIS)'}
                  onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value || null }))}
                >
                  <option value="">Untagged</option>
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div className="text-tertiary" style={{ display: 'grid', gap: 2, fontSize: 12 }}>
                <div>Raised {fmtDateTime(openTicket.created_at)} ({timeAgo(openTicket.created_at)})</div>
                {openTicket.picked_up_at && openTicket.status !== 'open' && (
                  <div>Picked up {fmtDateTime(openTicket.picked_up_at)}</div>
                )}
                <div>Last updated {fmtDateTime(openTicket.updated_at)}</div>
                {openTicket.status === 'closed' && openTicket.resolved_at && (
                  <div>
                    Closed {fmtDateTime(openTicket.resolved_at)}
                    {openTicket.closed_by ? ` by ${staffName(openTicket.closed_by)}` : ''}
                  </div>
                )}
              </div>
            </div>
            {canWrite && (
              <div className="modal__footer" style={{ alignItems: 'center' }}>
                {saveError && (
                  <span style={{ marginRight: 'auto', fontSize: 13, color: 'var(--status-danger, #ef4444)' }}>{saveError}</span>
                )}
                {!saveError && hasChanges && (
                  <span className="text-tertiary" style={{ marginRight: 'auto', fontSize: 12 }}>Unsaved changes</span>
                )}
                <button type="button" className="btn btn--ghost" onClick={closeTicketWindow}>
                  {hasChanges ? 'Cancel' : 'Close'}
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!hasChanges || updating === openTicket.id}
                  onClick={() => saveTicket(openTicket.id, pendingChanges)}
                >
                  {updating === openTicket.id ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
